/*
 * Lista de filtros para la lista de productos de proveedores.
 *
 * Contiene los componentes de buscador general, un selector de marcas, de subcategorías,
 * de proveedores. Tambien un componente para indicar las columnas visibles y un botón para
 * descargar un archivo PDF.
 *
 * Created on Tue Jul 18 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <b-form-row>

      <!-- #region::Searcher -->
      <b-col :md="searcherProductsWidth">
        <BasicSearcher
          ref="basic-searcher"
          :callback="handleSearch"
          placeholder="Buscar"
        />
      </b-col>
      <!-- #endregion end::Searcher -->

      <!-- #region::Brand filter -->
      <b-col
        v-if="canShowFilter('name_brand')"
        md="2"
      >
        <v-select
          v-model="selectedBrand"
          label="Name"
          class="style-chooser"
          placeholder="Marca"
          :options="getBrands"
        />
      </b-col>
      <!-- #endregion::Brand filter -->

      <!-- #region::Category filter -->
      <b-col
        v-if="canShowFilter('name_category')"
        md="2"
      >
        <v-select
          v-model="selectedCategory"
          label="Name"
          class="style-chooser"
          placeholder="Subcategoría"
          :options="getCategories"
        />
      </b-col>
      <!-- #endregion::Category filter -->

      <!-- #region::Category filter -->
      <b-col
        v-if="canShowFilter('NameSupplier')"
        md="2"
      >
        <v-select
          v-model="selectedSupplier"
          label="Name"
          class="style-chooser"
          placeholder="Proveedor"
          :options="getSuppliers"
          :disabled="!showSupplierComponents"
        />
      </b-col>
      <!-- #endregion::Category filter -->

      <!-- #region::Columns filter -->
      <b-col
        cols="12"
        md="auto"
        class="ml-auto"
      >
        <b-dropdown
          variant="outline-secondary"
          class="style-chooser"
          dropright
        >

          <template slot="button-content">
            <feather-icon
              icon="ColumnsIcon"
              class="mr-50"
            />
          </template>

          <b-dropdown-form>

            <!-- #region::Filter columns -->
            <BasicSearcher
              ref="filter-searcher"
              :callback="handleSearchColumns"
              placeholder="Buscar"
            />
            <!-- #endregion::Filter columns -->

            <b-form-checkbox
              v-for="(column, index) in filteredColumsBySearchTerm"
              :key="index"
              class="mt-50"
              :checked="column.active"
              @change="onColumnOptionChange(column)"
            >
              {{ column.text }}
            </b-form-checkbox>
          </b-dropdown-form>
        </b-dropdown>
      </b-col>
      <!-- #endregion::Columns filter -->

      <!-- #region::Download pdf button -->
      <b-col
        v-if="$ability.can('download pdf', 'Reports')"
        cols="12"
        md="auto"
      >
        <b-button
          variant="flat-success"
          class="csv-btn float-right"
          :disabled="downloadingFile"
          @click="onDownloadPDF"
        >
          <b-spinner
            v-if="downloadingFile"
            class="mr-50"
            small
          />
          <feather-icon
            v-if="!downloadingFile"
            icon="DownloadIcon"
            class="mr-50"
          />
          <span class="align-middle">Descargar PDF</span>
        </b-button>
      </b-col>
      <!-- #endregion::Download pdf button -->

      <!-- #region::Delete tab button -->
      <b-col
        v-if="!withoutSupplierTerm && !showSupplierComponents"
        cols="12"
        md="auto"
      >
        <b-button
          variant="delete-btn"
          class="delete-btn"
          @click="$emit('on:close-tab', title)"
        >
          Cerrar pestaña
        </b-button>
      </b-col>
      <!-- #endregion::Delete tab button -->

    </b-form-row>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BCol, BFormRow, BButton, BSpinner, BDropdown, BDropdownForm, BFormCheckbox,
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import BasicSearcher from '@/components/forms/BasicSearcher.vue'

const BRAND_NAME = 'name_brand'
const CATEGORY_NAME = 'name_category'
const SUPPLIER_NAME = 'NameSupplier'

export default {
  components: {
    BCol,
    BButton,
    vSelect,
    BSpinner,
    BFormRow,
    BDropdown,
    BasicSearcher,
    BDropdownForm,
    BFormCheckbox,
  },
  props: {
    /**
     * Titulo que se muestra en la pestaña
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Indica si se deben cargar los componentes relacionados con los proveedores
     */
    showSupplierComponents: {
      type: Boolean,
      default: true,
    },
    /**
     * Indica si se deben cargar los productos sin proveedor
     */
    withoutSupplierTerm: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      selectedBrand: null,
      selectedSupplier: null,
      selectedCategory: null,
      downloadingFile: false,
      columnsSearchTerm: '',
    }
  },
  computed: {
    ...mapGetters({
      getBrands: 'brands/getBrands',
      getSuppliers: 'suppliers/getSuppliers',
      getCategories: 'categories/getCategories',

      /**
       * Filtros globales
       */
      getListsParams: 'reportsSuppliers/getListsParams',
      getFilteredBrand: 'filters/getFilteredBrand',
      getFilteredSupplier: 'filters/getFilteredSupplier',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredSubcategory: 'filters/getFilteredSubcategory',
    }),
    /**
     * Marca filtrada (filtro en store)
     */
    filteredBrand: {
      get() { return this.getFilteredBrand },
    },
    /**
     * Proveedor filtrado (filtro en store)
     */
    filteredSupplier: {
      get() { return this.getFilteredSupplier },
    },
    /**
     * Término de búsqueda filtrado (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
    },
    /**
     * Subcategoría filtrada (filtro en store)
     */
    filteredSubcategory: {
      get() { return this.getFilteredSubcategory },
    },
    /**
     * Columnas de la pestala seleccionada, valida si se deben mostrar las columna de proveedor
     */
    filteredColumns: {
      get() {
        const { selectedColumns } = this.getListsParams.find(p => p.title === this.title)

        if (!this.$ability.can('filter columns_supplier', 'Reports')) {
          return selectedColumns.filter(c => c.value !== 'NameSupplier')
        }

        return selectedColumns
      },
    },
    /**
     * Columnas filtradas por término de búsqueda del filtro de columnas
     */
    filteredColumsBySearchTerm: {
      get() {
        return this.filteredColumns.filter(c => c.text.toLowerCase().includes(this.columnsSearchTerm.toLowerCase()))
      },
    },
    searcherProductsWidth: {
      get() {
        return (!this.withoutSupplierTerm && !this.showSupplierComponents)
          && this.canShowFilter('name_brand')
          && this.canShowFilter('name_category')
          && this.canShowFilter('NameSupplier') ? 2 : 3
      },
    },
  },
  mounted() {
    // Carga el término de búsqueda (filtro en store)
    if (this.filteredSearchTerm) {
      this.$refs['basic-searcher'].loadSearchTerm(this.filteredSearchTerm)
    }

    // Carga la marca filtrada (filtro en store)
    this.selectedBrand = this.filteredBrand ? { Name: this.filteredBrand } : null
    this.$watch('selectedBrand', brand => {
      this.$emit('on:filter', { type: 'brand', value: brand !== null ? brand.Name : '' })
    })

    // Carga el proveedor filtrado (filtro en store)
    this.selectedSupplier = this.filteredSupplier ? { Name: this.filteredSupplier } : null
    this.$watch('selectedSupplier', supplier => {
      this.$emit('on:filter', { type: 'supplier', value: supplier !== null ? supplier.Name : '' })
    })

    // Carga la subcategoría filtrada (filtro en store)
    this.selectedCategory = this.filteredSubcategory ? { Name: this.filteredSubcategory } : null
    this.$watch('selectedCategory', category => {
      this.$emit('on:filter', { type: 'category', value: category !== null ? category.Name : '' })
    })
  },
  methods: {
    ...mapActions({
      downloadPDFFile: 'reportsSuppliers/downloadPDFFile',
    }),
    /**
    * Evento del buscador básico
    *
    * @summary Emite un evento con el término de búsqueda y limpia los campos de selección
    * @param {String} searchTerm - Término de búsqueda
    */
    handleSearch(searchTerm) {
      this.$emit('on:filter', { type: 'search', value: searchTerm })

      this.selectedBrand = null
      this.selectedCategory = null
    },
    /**
    * Evento del buscador de filtro de columnas
    *
    * @summary Atiende el evento de búsqueda del filtro de columnas
    * @param {String} searchTerm - Término de búsqueda
    */
    handleSearchColumns(searchTerm) {
      this.columnsSearchTerm = searchTerm
    },

    /**
    * Selección de columnas
    * @summary Emite un evento con la columna seleccionada y su estado (activa o inactiva)
    * @param {Object} column - Columna seleccionada
    */
    onColumnOptionChange(column) {
      const baseColumn = column
      baseColumn.active = !baseColumn.active
      this.$emit('on:column-option-change', baseColumn)

      if (baseColumn.active) {
        return
      }

      // Si se desactiva la columna de categoria, limpia la categoria seleccionada
      if (baseColumn.value === CATEGORY_NAME && this.selectedCategory) {
        this.selectedCategory = null
      }

      // Si se desactiva la columna de marca, se limpia la marca seleccionada
      if (baseColumn.value === BRAND_NAME && this.selectedBrand) {
        this.selectedBrand = null
      }

      // Si se desactiva la columna de proveedor, se limpia el proveedor seleccionado
      if (baseColumn.value === SUPPLIER_NAME && this.selectedSupplier) {
        if (this.withoutSupplierTerm || this.showSupplierComponents) {
          this.selectedSupplier = null
        }
      }
    },

    /**
     * Proveedor aplicado al reporte (pestaña o selector), igual que loadParams en SuppliersList
     */
    resolveSupplierForDownload() {
      if (this.title === 'Todos') {
        if (this.selectedSupplier) {
          return this.selectedSupplier.Name
        }
        return this.filteredSupplier || ''
      }
      if (!this.withoutSupplierTerm && !this.showSupplierComponents) {
        return this.title
      }
      return this.selectedSupplier ? this.selectedSupplier.Name : ''
    },

    /**
     * Indica si hay filtros activos; con filtros el PDF incluye todo el resultado, no solo la página
     */
    hasActivePdfFilters() {
      return Boolean(
        this.filteredSearchTerm
        || (this.selectedBrand && this.selectedBrand.Name)
        || (this.selectedCategory && this.selectedCategory.Name)
        || this.resolveSupplierForDownload()
        || this.withoutSupplierTerm,
      )
    },

    /**
     * Descarga el archivo PDF
     *
     */
    async onDownloadPDF() {
      this.downloadingFile = true
      const activedColumns = this.filteredColumns.filter(c => c.active)
      const listParams = this.getListsParams.find(p => p.title === this.title) || {}
      const currentPage = listParams.currentPage || 1
      const perPage = listParams.perPage || 10
      const downloadAllFiltered = this.hasActivePdfFilters()

      let query = ''
      query += `filter[search]=${encodeURIComponent(this.filteredSearchTerm || '')}&`
      query += `brand=${encodeURIComponent(this.selectedBrand ? this.selectedBrand.Name : '')}&`
      query += `category=${encodeURIComponent(this.selectedCategory ? this.selectedCategory.Name : '')}&`
      query += `supplier=${encodeURIComponent(this.resolveSupplierForDownload())}&`
      query += `withoutSupplier=${this.withoutSupplierTerm ? 1 : 0}&`

      if (!downloadAllFiltered) {
        query += `page[number]=${currentPage}&`
        query += `limit=${perPage}&`
      }

      activedColumns.forEach((column, index) => {
        query += `active[${index}]=${column.value}${index < activedColumns.length - 1 ? '&' : ''}`
      })

      await this.downloadPDFFile(query)
      this.downloadingFile = false
    },

    /**
     * Indica si se debe mostrar el filtro de columnas
     *
     * @param {String} value - Valor de la columna
     */
    canShowFilter(value) {
      const checkedColumns = this.filteredColumns.filter(c => c.active && c.value === value)
      return checkedColumns.length > 0
    },
  },
}
</script>

<style lang="scss">
.btn-outline-secondary {
  border: 1px solid !important;
  border-color: #c9c9c9 !important;
}
.dropdown-menu {
  min-width: 16rem !important;
}
.vs__selected button {
  display: none
}
</style>
